// pages/strategic-playbook/tools/cost-of-action.js
// ════════════════════════════════════════════════════
// COST-OF-ACTION CALCULATOR
// ════════════════════════════════════════════════════
// Surface the AND. Brady has 7 rings AND a divorce.
// Every decision has costs across multiple dimensions.
// Most leaders under-account for them.
// ════════════════════════════════════════════════════

import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const TIME_HORIZONS = [
  { id: 'short', label: 'Short-term', detail: 'Days to weeks' },
  { id: 'medium', label: 'Medium-term', detail: 'Months to a year' },
  { id: 'long', label: 'Long-term', detail: 'Multi-year' },
  { id: 'multi-decade', label: 'Multi-decade', detail: '10+ years' },
];

const STAGES = [
  { id: 'early-career', label: 'Early-career', detail: '20s, building foundation' },
  { id: 'mid-career', label: 'Mid-career', detail: '30s-40s, peak earning + obligations' },
  { id: 'late-career', label: 'Late-career', detail: '50s+, capital preservation' },
  { id: 'founder', label: 'Founder mode', detail: 'Building, all-in on the venture' },
  { id: 'transition', label: 'In transition', detail: 'Job change, life change, pivot' },
];

const WEIGHT_COLORS = {
  Heavy: 'var(--sp-oxblood)',
  Moderate: 'var(--sp-cognac)',
  Light: 'var(--sp-forest-moss)',
};

export default function CostOfActionTool() {
  const [decision, setDecision] = useState('');
  const [timeHorizon, setTimeHorizon] = useState('medium');
  const [userStage, setUserStage] = useState('mid-career');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function generate() {
    if (decision.trim().length < 10) {
      setError('Describe the decision in more detail (at least 10 characters).');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/strategic-playbook/cost-of-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          timeHorizon: TIME_HORIZONS.find((t) => t.id === timeHorizon)?.label || timeHorizon,
          userStage: STAGES.find((s) => s.id === userStage)?.label || userStage,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sp-root">
      <Head>
        <title>Cost-of-Action Calculator · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>
            ← Tools
          </Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Strategic Framing · Generator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Cost-of-Action Calculator
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Surface the <strong>AND</strong>. Brady has 7 rings AND a divorce. Every
            decision has costs across time, optionality, identity, relationships, capital,
            and energy. Most leaders under-account for them. This tool surfaces what
            you&apos;re really committing to — and the do-nothing baseline you&apos;re ignoring.
          </p>
        </div>

        {/* Input form */}
        <div
          style={{
            padding: '28px',
            background: 'var(--sp-soft-charcoal)',
            border: '1px solid var(--sp-divider)',
            borderRadius: 'var(--sp-radius-card)',
            marginBottom: 24,
          }}
        >
          <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
            The Decision
          </h3>

          <textarea
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            placeholder="Describe what you're considering. Example: 'Take a senior VP role at a public company. $400K base + $200K equity. Reports to CEO directly. Means relocating family to Boston, kids change schools, wife gives up her current consulting practice.'"
            rows={6}
            style={{
              width: '100%',
              padding: '14px 18px',
              background: 'var(--sp-charcoal-black)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius)',
              color: 'var(--sp-warm-cream)',
              fontFamily: 'var(--sp-font-body)',
              fontSize: 14,
              lineHeight: 1.6,
              resize: 'vertical',
              outline: 'none',
              marginBottom: 18,
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="cost-options">
            <OptionGroup
              label="Time Horizon"
              options={TIME_HORIZONS}
              selected={timeHorizon}
              onChange={setTimeHorizon}
            />
            <OptionGroup
              label="Your Life Stage"
              options={STAGES}
              selected={userStage}
              onChange={setUserStage}
            />
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={generate}
              disabled={loading || decision.trim().length < 10}
              style={{
                padding: '12px 24px',
                background: loading || decision.trim().length < 10 ? 'transparent' : 'var(--sp-brass-bg)',
                border: `1px solid ${loading || decision.trim().length < 10 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                color: loading || decision.trim().length < 10 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                cursor: loading || decision.trim().length < 10 ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Surfacing the AND...' : 'Calculate Cost →'}
            </button>
            {error && (
              <div style={{ fontSize: 12, color: 'var(--sp-oxblood)', fontFamily: 'var(--sp-font-mono)', letterSpacing: '0.04em' }}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div
            style={{
              padding: '48px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
              textAlign: 'center',
              fontFamily: 'var(--sp-font-serif)',
              fontStyle: 'italic',
              color: 'var(--sp-aged-brass)',
              fontSize: 16,
            }}
          >
            Surfacing the AND...
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontStyle: 'normal', fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 8, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Honest Read · 6 cost dimensions
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div>
            {/* Summary */}
            <div
              style={{
                padding: '28px',
                background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
                border: '1px solid var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 20,
              }}
            >
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
                Dominant Cost Pattern
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontStyle: 'italic',
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: 'var(--sp-warm-cream)',
                  borderLeft: '2px solid var(--sp-aged-brass)',
                  paddingLeft: 16,
                }}
              >
                {result.summary}
              </div>
            </div>

            {/* One-way door warning */}
            {result.oneWayDoorWarning && (
              <div
                style={{
                  padding: '20px 24px',
                  background: 'rgba(74, 31, 31, 0.18)',
                  border: '1px solid var(--sp-oxblood)',
                  borderRadius: 'var(--sp-radius-card)',
                  marginBottom: 20,
                }}
              >
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-oxblood)', marginBottom: 10 }}>
                  ⚠ One-Way Door Warning
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--sp-warm-cream)' }}>
                  {result.oneWayDoorWarning}
                </div>
              </div>
            )}

            {/* Cost dimensions */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', borderTop: '1px solid var(--sp-aged-brass)', paddingTop: 12, marginBottom: 14 }}>
                What This Costs
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(result.costs || []).map((c, i) => (
                  <CostCard key={i} cost={c} />
                ))}
              </div>
            </div>

            {/* Reversibility + recovery */}
            <div
              style={{
                padding: '24px 28px',
                background: 'var(--sp-soft-charcoal)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 20,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 20,
              }}
            >
              <div>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6 }}>
                  Reversibility
                </div>
                <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 22, color: result.reversibility === 'One-way' ? 'var(--sp-oxblood)' : result.reversibility === 'Partial' ? 'var(--sp-cognac)' : 'var(--sp-forest-moss)' }}>
                  {result.reversibility}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6 }}>
                  Time to Recover If Wrong
                </div>
                <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 22, color: 'var(--sp-warm-cream)' }}>
                  {result.timeToRecoverMonths}
                </div>
              </div>
            </div>

            {/* Do-nothing baseline */}
            <div
              style={{
                padding: '20px 24px',
                background: 'var(--sp-parchment-bg)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
                Do-Nothing Baseline
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.85)' }}>
                {result.doNothingBaseline}
              </div>
            </div>

            {/* Honest read */}
            <div
              style={{
                padding: '20px 24px',
                background: 'rgba(74, 31, 31, 0.10)',
                border: '1px solid rgba(74, 31, 31, 0.30)',
                borderRadius: 'var(--sp-radius-card)',
              }}
            >
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-oxblood)', marginBottom: 10 }}>
                The Brutal Truth
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.65, color: 'var(--sp-warm-cream)' }}>
                {result.honestRead}
              </div>
            </div>

            {/* Connected dossiers */}
            <div style={{ marginTop: 20, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
                Connected:
              </span>
              <Link href="/strategic-playbook/library" style={{ color: 'var(--sp-aged-brass)' }}>
                Honest Read framework (every dossier)
              </Link>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media (max-width: 700px) {
          .cost-options {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function OptionGroup({ label, options, selected, onChange }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 10 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              textAlign: 'left',
              padding: '8px 12px',
              background: selected === opt.id ? 'var(--sp-brass-bg)' : 'transparent',
              border: `1px solid ${selected === opt.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
              borderRadius: 'var(--sp-radius)',
              cursor: 'pointer',
              fontSize: 13,
              color: selected === opt.id ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.75)',
            }}
          >
            <span style={{ fontFamily: 'var(--sp-font-serif)' }}>{opt.label}</span>
            <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)', marginLeft: 8 }}>
              {opt.detail}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CostCard({ cost }) {
  const color = WEIGHT_COLORS[cost.weight] || 'var(--sp-aged-brass)';
  return (
    <div
      style={{
        padding: '16px 20px',
        background: 'var(--sp-soft-charcoal)',
        border: '1px solid var(--sp-divider)',
        borderRadius: 'var(--sp-radius-card)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          height: '100%',
          background: color,
          opacity: 0.7,
        }}
      />
      <div style={{ paddingLeft: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
          }}
        >
          <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 16, color: 'var(--sp-warm-cream)' }}>
            {cost.category}
          </div>
          <span
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              padding: '3px 8px',
              border: `1px solid ${color}`,
              borderRadius: 'var(--sp-radius)',
              color: color,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
            }}
          >
            {cost.weight}
          </span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(232, 221, 196, 0.78)' }}>
          {cost.cost}
        </div>
      </div>
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Cost-of-Action Calculator</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
